import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { UserIcon } from 'lucide-react';
function StaticResumeVisual() {
  return <div className="relative w-full max-w-sm mx-auto opacity-60 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-76">
      <div className="bg-white rounded-lg shadow-xl p-6 h-[400px] border border-slate-200 relative overflow-hidden" data-id="element-77">
        {/* Header */}
        <div className="flex items-start gap-4 mb-6" data-id="element-78">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-79">
            <UserIcon className="w-8 h-8 text-slate-400" data-id="element-80" />
          </div>
          <div className="flex-1 space-y-2" data-id="element-81">
            <div className="h-4 bg-slate-800 rounded w-3/4" data-id="element-82" />
            <div className="h-3 bg-slate-300 rounded w-1/2" data-id="element-83" />
          </div>
        </div>

        {/* Body Lines */}
        <div className="space-y-4" data-id="element-84">
          <div className="space-y-2" data-id="element-85">
            <div className="h-3 bg-slate-200 rounded w-1/4" data-id="element-86" />
            <div className="h-2 bg-slate-100 rounded w-full" data-id="element-87" />
            <div className="h-2 bg-slate-100 rounded w-full" data-id="element-88" />
            <div className="h-2 bg-slate-100 rounded w-5/6" data-id="element-89" />
          </div>

          <div className="space-y-2 pt-2" data-id="element-90">
            <div className="h-3 bg-slate-200 rounded w-1/3" data-id="element-91" />
            <div className="h-2 bg-slate-100 rounded w-full" data-id="element-92" />
            <div className="h-2 bg-slate-100 rounded w-4/5" data-id="element-93" />
          </div>

          <div className="space-y-2 pt-2" data-id="element-94">
            <div className="h-3 bg-slate-200 rounded w-1/4" data-id="element-95" />
            <div className="flex gap-2" data-id="element-96">
              <div className="h-6 bg-slate-100 rounded w-16" data-id="element-97" />
              <div className="h-6 bg-slate-100 rounded w-20" data-id="element-98" />
              <div className="h-6 bg-slate-100 rounded w-12" data-id="element-99" />
            </div>
          </div>
        </div>

        {/* Static Label */}
        <div className="absolute top-4 right-4" data-id="element-100">
          <span className="text-[10px] font-mono text-slate-400 border border-slate-200 px-2 py-1 rounded uppercase" data-id="element-101">
            Static Data
          </span>
        </div>
      </div>
    </div>;
}
export function CoreShiftSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const capabilities = ['capability under pressure', 'environmental adaptability', 'fatigue and cognitive load', 'real-world execution performance'];
  return <section ref={ref} className="w-full bg-slate-900 py-24 lg:py-32 relative" data-id="element-102">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-103">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center" data-id="element-104">
          {/* Left — Split Visual (Moved from Right) */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative h-[500px] w-full order-2 lg:order-1" data-id="element-105">
            {/* Left: Static Resume (Smaller, Top-Left) */}
            <div className="absolute top-0 left-0 w-[40%] z-0 transform scale-75 origin-top-left opacity-80 hover:opacity-100 transition-opacity duration-300" data-id="element-106">
              <StaticResumeVisual data-id="element-107" />
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-center w-full" data-id="element-108">
                <span className="text-xs font-mono text-slate-500 uppercase tracking-wider" data-id="element-109">
                  The Old Way
                </span>
              </div>
            </div>

            {/* Right: Live Dashboard (Larger, Bottom-Right) */}
            <div className="absolute bottom-0 right-0 w-[85%] z-10" data-id="element-110">
              <div className="rounded-xl overflow-hidden shadow-2xl border border-tiq-purple/30 bg-slate-900 relative transform hover:scale-105 transition-transform duration-500 origin-bottom-right" data-id="element-111">
                <img src="https://cdn.magicpatterns.com/uploads/1rVYXkMHy2CBJ9XVz8GWo2/Screen_Shot_2025-11-21_at_9.16.29_AM.png" alt="TalentIQ Readiness Index" className="w-full h-auto" data-id="element-112" />
              </div>
              {/* Glow behind the active dashboard */}
              <div className="absolute inset-0 bg-tiq-purple/20 blur-2xl -z-10" data-id="element-113" />

              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-center w-full" data-id="element-114">
                <span className="text-xs font-mono text-tiq-purple uppercase tracking-wider font-bold" data-id="element-115">
                  TalentIQ Readiness
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right — Copy (Moved from Left) */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8
        }} className="order-1 lg:order-2" data-id="element-116">
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight tracking-tight mb-6" data-id="element-117">
              Hiring Data Doesn't Equal Readiness.
            </h2>

            <p className="text-lg text-slate-300 leading-relaxed mb-4" data-id="element-118">
              Traditional workforce platforms track credentials and hiring
              pipelines.
            </p>

            <p className="text-lg text-slate-300 leading-relaxed mb-8" data-id="element-119">
              But operational success depends on something deeper:
            </p>

            <ul className="space-y-3 mb-8" data-id="element-120">
              {capabilities.map((cap, i) => <motion.li key={cap} className="flex items-center gap-3" initial={{
              opacity: 0,
              x: -20
            }} animate={isInView ? {
              opacity: 1,
              x: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.3 + i * 0.1
            }} data-id="element-121">
                  <div className="w-1.5 h-1.5 rounded-full bg-tiq-green flex-shrink-0" data-id="element-122" />
                  <span className="text-tiq-blue font-medium" data-id="element-123">{cap}</span>
                </motion.li>)}
            </ul>

            <p className="text-lg text-slate-300 leading-relaxed" data-id="element-124">
              TalentIQ transforms human complexity into measurable readiness
              intelligence —{' '}
              <span className="text-tiq-purple font-medium" data-id="element-125">
                before risk appears.
              </span>
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}